import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Plus, CreditCard, Banknote, Building2, 
  Smartphone, CheckCircle, XCircle, Clock, RefreshCw, Download,
  Filter, Calendar, Eye, MoreHorizontal, TrendingDown, AlertTriangle, Percent, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

function AdminPayments({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [filters, setFilters] = useState({
    status: 'all',
    method: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  
  const [showCreatePayment, setShowCreatePayment] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  const [orders, setOrders] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [recovering, setRecovering] = useState('');
  const [funnel, setFunnel] = useState(null);
  const [funnelDays, setFunnelDays] = useState('30');
  const [newPayment, setNewPayment] = useState({
    order_id: '',
    amount: '',
    payment_method: 'cash',
    notes: ''
  });

  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Наличные', icon: Banknote, color: 'bg-green-100 text-green-800' },
    { value: 'card', label: 'Карта', icon: CreditCard, color: 'bg-blue-100 text-blue-800' },
    { value: 'transfer', label: 'Перевод', icon: Building2, color: 'bg-purple-100 text-purple-800' },
    { value: 'kaspi_apipay', label: 'Kaspi (ApiPay)', icon: Smartphone, color: 'bg-red-100 text-red-800' },
    { value: 'online', label: 'Онлайн', icon: Smartphone, color: 'bg-orange-100 text-orange-800' },
  ];

  const PAYMENT_STATUSES = [
    { value: 'pending', label: 'Ожидает', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'paid', label: 'Оплачено', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
    { value: 'failed', label: 'Ошибка', icon: XCircle, color: 'bg-red-100 text-red-800' },
    { value: 'refunded', label: 'Возврат', icon: RefreshCw, color: 'bg-gray-100 text-gray-800' },
  ];

  useEffect(() => {
    fetchPayments();
    fetchOrders();
    fetchOrphans();
  }, [page, filters]);

  useEffect(() => {
    fetchFunnel();
  }, [funnelDays]);

  const fetchFunnel = async () => {
    try {
      const res = await axios.get(`${API}/payments/admin/funnel?days=${funnelDays}`, { headers: getAuthHeaders() });
      setFunnel(res.data);
    } catch (error) {
      // Non-blocking: funnel section just stays hidden
    }
  };

  const fetchOrphans = async () => {
    try {
      const res = await axios.get(`${API}/payments/admin/orphan-intents`, { headers: getAuthHeaders() });
      setOrphans(res.data.intents || []);
    } catch (error) {
      // Non-blocking: section just stays hidden
    }
  };

  const handleRecoverIntent = async (invoiceId) => {
    setRecovering(invoiceId);
    try {
      const res = await axios.post(`${API}/payments/admin/recover-intent/${invoiceId}`, {}, { headers: getAuthHeaders() });
      toast.success(`Заказ восстановлен (№ заказа создан)`);
      await fetchOrphans();
      await fetchPayments();
      await fetchFunnel();
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось восстановить заказ');
    } finally {
      setRecovering('');
    }
  };

  const fetchPayments = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.method !== 'all' && { method: filters.method }),
        ...(filters.dateFrom && { date_from: filters.dateFrom }),
        ...(filters.dateTo && { date_to: filters.dateTo }),
        ...(filters.search && { search: filters.search }),
      });
      
      const response = await axios.get(`${API}/admin/payments?${params}`, {
        headers: getAuthHeaders()
      });
      
      setPayments(response.data.payments);
      setSummary(response.data.summary);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Не удалось загрузить платежи');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeaders() });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!newPayment.order_id || !newPayment.amount) {
      toast.error('Заполните обязательные поля');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/payments`, {
        order_id: newPayment.order_id,
        amount: parseFloat(newPayment.amount),
        payment_method: newPayment.payment_method,
        notes: newPayment.notes
      }, { headers: getAuthHeaders() });
      
      toast.success('Платёж записан');
      setShowCreatePayment(false);
      setNewPayment({ order_id: '', amount: '', payment_method: 'cash', notes: '' });
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка при записи платежа');
    }
  };

  const handleStatusChange = async (paymentId, newStatus) => {
    try {
      await axios.put(`${API}/admin/payments/${paymentId}/status?status=${newStatus}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Статус обновлён');
      fetchPayments();
    } catch (error) {
      toast.error('Не удалось обновить статус');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU').format(amount || 0) + ' ₸';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMethodConfig = (method) => {
    return PAYMENT_METHODS.find(m => m.value === method) || PAYMENT_METHODS[0];
  };

  const getStatusConfig = (status) => {
    return PAYMENT_STATUSES.find(s => s.value === status) || PAYMENT_STATUSES[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка платежей...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Платежи и транзакции</h1>
                <p className="text-sm text-gray-500">Управление финансовыми операциями</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button 
                onClick={() => setShowCreatePayment(true)} 
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="add-payment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Записать платёж
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* ============ Kaspi/ApiPay Payment Funnel ============ */}
        {funnel && (
          <Card className="mb-6 border-emerald-200" data-testid="payment-funnel-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-700" />
                    Воронка онлайн-оплат (Kaspi / ApiPay)
                  </CardTitle>
                  <CardDescription>Конверсия платежей в заказы и упущенная выручка</CardDescription>
                </div>
                <Select value={funnelDays} onValueChange={setFunnelDays}>
                  <SelectTrigger className="w-[150px]" data-testid="funnel-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 дней</SelectItem>
                    <SelectItem value="30">30 дней</SelectItem>
                    <SelectItem value="90">90 дней</SelectItem>
                    <SelectItem value="365">Год</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {/* KPI cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3" data-testid="funnel-conversion">
                  <div className="text-xs text-emerald-700 flex items-center gap-1"><Percent className="w-3 h-3" /> Конверсия</div>
                  <div className="text-2xl font-bold text-emerald-700">{funnel.summary.conversion_rate}%</div>
                  <div className="text-xs text-gray-500">{funnel.summary.paid_count} из {funnel.summary.total_intents} оплат</div>
                </div>
                <div className="rounded-xl border bg-green-50 border-green-200 p-3" data-testid="funnel-paid">
                  <div className="text-xs text-green-700 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Оплачено</div>
                  <div className="text-2xl font-bold text-green-700">{formatCurrency(funnel.summary.paid_amount)}</div>
                  <div className="text-xs text-gray-500">{funnel.summary.paid_count} платежей</div>
                </div>
                <div className="rounded-xl border bg-red-50 border-red-200 p-3" data-testid="funnel-lost">
                  <div className="text-xs text-red-700 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Упущено</div>
                  <div className="text-2xl font-bold text-red-700">{formatCurrency(funnel.summary.lost_amount)}</div>
                  <div className="text-xs text-gray-500">{funnel.summary.failed_count} ошибок · {funnel.summary.processing_count} зависло</div>
                </div>
                <div className="rounded-xl border bg-amber-50 border-amber-200 p-3" data-testid="funnel-orphan">
                  <div className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Оплачено без заказа</div>
                  <div className="text-2xl font-bold text-amber-700">{funnel.summary.paid_no_order_count}</div>
                  <div className="text-xs text-gray-500">{formatCurrency(funnel.summary.paid_no_order_amount)} — нужно восстановить</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Daily trend */}
                <div className="rounded-xl border p-3" data-testid="funnel-trend">
                  <div className="text-sm font-medium text-gray-700 mb-2">Создано vs Оплачено по дням</div>
                  {funnel.daily.length > 0 ? (
                    <ResponsiveContainer width="100%" height={160}>
                      <AreaChart data={funnel.daily} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.5} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="day" tickFormatter={(d) => d?.slice(5)} fontSize={10} />
                        <YAxis fontSize={10} allowDecimals={false} />
                        <Tooltip />
                        <Area type="monotone" dataKey="created" name="Создано" stroke="#94a3b8" fill="url(#gCreated)" strokeWidth={2} />
                        <Area type="monotone" dataKey="paid" name="Оплачено" stroke="#059669" fill="url(#gPaid)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-sm text-gray-400 py-8 text-center">Нет данных за период</div>
                  )}
                </div>

                {/* Failure reasons */}
                <div className="rounded-xl border p-3" data-testid="funnel-reasons">
                  <div className="text-sm font-medium text-gray-700 mb-2">Причины неудачных оплат</div>
                  {funnel.failure_reasons.length > 0 ? (
                    <div className="space-y-1.5">
                      {funnel.failure_reasons.slice(0, 6).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm gap-2" data-testid={`funnel-reason-${i}`}>
                          <span className="truncate text-gray-700">{r.reason}</span>
                          <span className="shrink-0 text-gray-500">{r.count} · {formatCurrency(r.amount)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 py-8 text-center">Неудачных оплат нет 🎉</div>
                  )}
                </div>
              </div>

              {/* Stuck (abandoned) intents */}
              {funnel.stuck && funnel.stuck.length > 0 && (
                <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-3" data-testid="funnel-stuck">
                  <div className="text-sm font-medium text-orange-800 mb-2 flex items-center gap-1">
                    <Clock className="w-4 h-4" /> Зависшие оплаты ({funnel.stuck.length}) — клиент начал оплату, но не завершил
                  </div>
                  <div className="space-y-2">
                    {funnel.stuck.map((s) => (
                      <div key={s.invoice_id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-orange-200 px-3 py-2" data-testid={`stuck-row-${s.invoice_id}`}>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{s.user_name} <span className="text-gray-400">·</span> {s.phone || 'без телефона'}</div>
                          <div className="text-xs text-gray-500">
                            Инвойс {s.invoice_id} · {formatCurrency(s.amount)} · {s.items_qty} ед. · {formatDate(s.created_at)}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRecoverIntent(s.invoice_id)}
                          disabled={recovering === s.invoice_id}
                          className="border-orange-300 text-orange-700 hover:bg-orange-100 shrink-0"
                          data-testid={`stuck-recover-btn-${s.invoice_id}`}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${recovering === s.invoice_id ? 'animate-spin' : ''}`} />
                          Проверить / восстановить
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="total-amount-card">
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Всего транзакций</div>
              <div className="text-2xl font-bold">{formatCurrency(summary.total_amount)}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500" data-testid="completed-amount-card">
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Получено</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.completed_amount)}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500" data-testid="pending-amount-card">
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Ожидает</div>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency(summary.pending_amount)}</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-500" data-testid="refunded-amount-card">
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500">Возвраты</div>
              <div className="text-2xl font-bold text-gray-600">{formatCurrency(summary.refunded_amount)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Orphan paid intents — money taken but order missing */}
        {orphans.length > 0 && (
          <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="orphan-intents-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                <XCircle className="w-5 h-5" />
                Потерянные оплаты — заказ не создан ({orphans.length})
              </CardTitle>
              <CardDescription className="text-amber-700">
                Клиент начал оплату через Kaspi, но заказ не сформировался (закрыл страницу, либо webhook не дошёл). Нажмите «Восстановить» — статус будет перепроверен в ApiPay, и если оплата прошла, заказ создастся из сохранённой корзины.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {orphans.map((o) => (
                <div key={o.invoice_id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-200 px-3 py-2" data-testid={`orphan-row-${o.invoice_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {o.user_name || o.user_email || '—'} <span className="text-gray-400">·</span> {o.user_email}
                    </div>
                    <div className="text-xs text-gray-500">
                      Инвойс {o.invoice_id} · {formatCurrency(o.amount)} · {o.user_phone || 'без телефона'}
                    </div>
                    <div className="mt-0.5">
                      {o.needs_verification ? (
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700" data-testid={`orphan-status-${o.invoice_id}`}>
                          ⚠️ статус «{o.status}» — проверить оплату в ApiPay
                        </span>
                      ) : (
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700" data-testid={`orphan-status-${o.invoice_id}`}>
                          ✓ оплачено, заказ не создан
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleRecoverIntent(o.invoice_id)}
                    disabled={recovering === o.invoice_id}
                    className="bg-amber-600 hover:bg-amber-700 shrink-0"
                    data-testid={`recover-btn-${o.invoice_id}`}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${recovering === o.invoice_id ? 'animate-spin' : ''}`} />
                    {recovering === o.invoice_id ? 'Восстанавливаю...' : 'Восстановить заказ'}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <Label className="text-sm text-gray-600">Поиск</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Номер заказа или клиент..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">Статус</Label>
                <Select 
                  value={filters.status} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все статусы</SelectItem>
                    {PAYMENT_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">Способ оплаты</Label>
                <Select 
                  value={filters.method} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, method: v }))}
                >
                  <SelectTrigger data-testid="method-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все способы</SelectItem>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">С даты</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  data-testid="date-from-input"
                />
              </div>

              <div className="w-[150px]">
                <Label className="text-sm text-gray-600">По дату</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  data-testid="date-to-input"
                />
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({
                  status: 'all',
                  method: 'all',
                  dateFrom: '',
                  dateTo: '',
                  search: ''
                })}
                data-testid="reset-filters-btn"
              >
                Сбросить
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payments-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Дата</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Заказ</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Клиент</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Способ</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Сумма</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Статус</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg font-medium">Нет платежей</p>
                        <p className="text-sm">Записи о платежах появятся здесь</p>
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => {
                      const methodConfig = getMethodConfig(payment.payment_method);
                      const statusConfig = getStatusConfig(payment.status);
                      const MethodIcon = methodConfig.icon;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={payment.id} className="hover:bg-gray-50" data-testid={`payment-row-${payment.id}`}>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(payment.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm font-medium text-emerald-700">
                              {payment.order_number}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{payment.customer_name}</div>
                            <Badge variant="outline" className="text-xs mt-1">
                              {payment.customer_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={methodConfig.color}>
                              <MethodIcon className="w-3 h-3 mr-1" />
                              {methodConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-lg font-semibold">{formatCurrency(payment.amount)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`payment-actions-${payment.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setSelectedPayment(payment);
                                  setShowPaymentDetails(true);
                                }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Подробнее
                                </DropdownMenuItem>
                                {payment.status === 'pending' && (
                                  <DropdownMenuItem onClick={() => handleStatusChange(payment.id, 'paid')}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                    Отметить как оплачено
                                  </DropdownMenuItem>
                                )}
                                {payment.status === 'paid' && (
                                  <DropdownMenuItem onClick={() => handleStatusChange(payment.id, 'refunded')}>
                                    <RefreshCw className="w-4 h-4 mr-2 text-orange-600" />
                                    Оформить возврат
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-500">
              Показано {payments.length} из {total} платежей
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Назад
              </Button>
              <span className="px-3 py-1 text-sm">
                Страница {page} из {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Далее
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Payment Dialog */}
      <Dialog open={showCreatePayment} onOpenChange={setShowCreatePayment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Записать платёж</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreatePayment} className="space-y-4">
            <div>
              <Label>Заказ *</Label>
              <Select
                value={newPayment.order_id}
                onValueChange={(v) => {
                  const order = orders.find(o => o.id === v);
                  setNewPayment(prev => ({
                    ...prev,
                    order_id: v,
                    amount: order ? order.total_amount.toString() : ''
                  }));
                }}
              >
                <SelectTrigger data-testid="order-select">
                  <SelectValue placeholder="Выберите заказ" />
                </SelectTrigger>
                <SelectContent>
                  {orders.map(order => (
                    <SelectItem key={order.id} value={order.id}>
                      <span className="font-mono">{order.order_number}</span>
                      <span className="ml-2 text-gray-500">— {order.user_name}</span>
                      <span className="ml-2 font-medium">{formatCurrency(order.total_amount)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Сумма *</Label>
              <Input
                type="number"
                step="0.01"
                value={newPayment.amount}
                onChange={(e) => setNewPayment(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                required
                data-testid="amount-input"
              />
            </div>

            <div>
              <Label>Способ оплаты</Label>
              <Select
                value={newPayment.payment_method}
                onValueChange={(v) => setNewPayment(prev => ({ ...prev, payment_method: v }))}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Примечание</Label>
              <Input
                value={newPayment.notes}
                onChange={(e) => setNewPayment(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Дополнительная информация..."
                data-testid="notes-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreatePayment(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="submit-payment-btn">
                Записать платёж
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Details Dialog */}
      <Dialog open={showPaymentDetails} onOpenChange={setShowPaymentDetails}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Детали платежа</DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Заказ</Label>
                  <p className="font-mono font-medium text-emerald-700">{selectedPayment.order_number}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Сумма</Label>
                  <p className="text-xl font-bold">{formatCurrency(selectedPayment.amount)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Клиент</Label>
                  <p className="font-medium">{selectedPayment.customer_name}</p>
                  <Badge variant="outline" className="mt-1">{selectedPayment.customer_type}</Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Способ оплаты</Label>
                  <Badge className={getMethodConfig(selectedPayment.payment_method).color}>
                    {getMethodConfig(selectedPayment.payment_method).label}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Статус</Label>
                  <Badge className={getStatusConfig(selectedPayment.status).color}>
                    {getStatusConfig(selectedPayment.status).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Дата создания</Label>
                  <p className="text-sm">{formatDate(selectedPayment.created_at)}</p>
                </div>
              </div>
              
              {selectedPayment.processed_at && (
                <div>
                  <Label className="text-gray-500">Дата обработки</Label>
                  <p className="text-sm">{formatDate(selectedPayment.processed_at)}</p>
                </div>
              )}
              
              {selectedPayment.notes && (
                <div>
                  <Label className="text-gray-500">Примечание</Label>
                  <p className="text-sm bg-gray-50 p-2 rounded">{selectedPayment.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPayments;
